import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { AnimatedInput } from '@/components/ui/animated-input';
import { AnimatedSelect } from '@/components/ui/animated-select';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import '@/styles/survey.css';
import '@/styles/checkbox.css';
import { Seo } from '@/components/Seo';


type SurveyFormValues = {
  contact_name: string;
  company_name: string;
  email: string;
  phone: string;
  location: string;
  years_in_business: string;
  team_size: string;
  business_type: string[];
  quotes_per_month: string;
  time_per_quote: string;
  quoting_challenges: string[];
  current_tools: string[];
  provide_beta_feedback: string;
  feedback_method: string;
  additional_comments: string;
};

const steps = [
  { id: 1, title: "Contact Information" },
  { id: 2, title: "Business Information" },
  { id: 3, title: "Quoting Process" },
  { id: 4, title: "Tools and Technology" },
  { id: 5, title: "Feedback" }
];

const purpleTheme = {
  primary: '#3926B1',
  secondary: '#B765D2',
  gradient: 'linear-gradient(135deg, #3926B1 0%, #B765D2 100%)',
  buttonHover: 'linear-gradient(135deg, #4A37C2 0%, #C576E3 100%)',
  inputBorder: '#E2D9FF',
  inputFocus: '#B765D2',
  cardBg: '#F9F7FF',
  error: '#FF4D4F',
};

const Survey = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState(1);
  
  const location = useLocation();
  const navigate = useNavigate();
  
  const { register, handleSubmit, watch, control, formState: { errors }, trigger } = useForm<SurveyFormValues>({
    defaultValues: {
      business_type: [],
      quoting_challenges: [],
      current_tools: []
    }
  });
  const watchWillingToProvideFeedback = watch('provide_beta_feedback');
  
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tokenFromUrl = searchParams.get('token');
    
    if (!tokenFromUrl) {
      setError('No access token provided');
      setIsLoading(false);
      return;
    }
    
    setToken(tokenFromUrl);
    
    const verifyToken = async () => {
      try {
        const { data, error } = await supabase.rpc('verify_survey_token', {
          token_to_check: tokenFromUrl
        });
        
        if (error) {
          console.error('Error verifying token:', error);
          setError('Error verifying access token');
          setIsValid(false);
        } else {
          setIsValid(data);
          if (!data) {
            setError('Invalid or expired access token');
          }
        }
      } catch (err) {
        console.error('Error verifying token:', err);
        setError('Error verifying access token');
        setIsValid(false);
      } finally {
        setIsLoading(false);
      }
    };
    
    verifyToken();
  }, [location]);
  
  const sendConfirmationEmail = async (name: string, email: string, companyName?: string) => {
    try {
      console.log('Sending confirmation email to:', email);
      
      const { data, error } = await supabase.functions.invoke(
        'survey-confirmation',
        {
          body: JSON.stringify({
            name,
            email,
            companyName
          }),
        }
      );
      
      console.log('Email API response:', data);
      
      if (error) {
        console.error('Failed to send confirmation email:', error);
        throw new Error(`Failed to send email: ${error.message}`);
      } else {
        console.log('Confirmation email sent successfully');
        return true;
      }
    } catch (err) {
      console.error('Error sending confirmation email:', err);
      throw err; // Re-throw to be handled by caller
    }
  };
  
  const onSubmit = async (data: SurveyFormValues) => {
    setIsSubmitting(true);
    setError('');
    
    try {
      let ipAddress = 'unknown';
      try {
        const response = await fetch('https://api.ipify.org?format=json');
        const ipData = await response.json();
        ipAddress = ipData.ip;
      } catch (err) {
        console.error('Could not get IP address:', err);
      }
      
      const formattedData = {
        contact_name: data.contact_name,
        company_name: data.company_name,
        email: data.email,
        phone: data.phone,
        location: data.location,
        years_in_business: data.years_in_business,
        team_size: data.team_size,
        business_type: data.business_type,
        quotes_per_month: data.quotes_per_month,
        time_per_quote: data.time_per_quote,
        quoting_challenges: data.quoting_challenges,
        current_tools: data.current_tools,
        willing_to_provide_feedback: data.provide_beta_feedback === 'yes',
        feedback_method: data.feedback_method,
        additional_comments: data.additional_comments,
        ip_address: ipAddress
      };
      
      console.log('Submitting data:', formattedData);
      
      const { error: submitError } = await supabase
        .from('survey_responses')
        .insert(formattedData);
      
      if (submitError) {
        console.error('Error submitting survey:', submitError);
        setError('There was an error submitting your survey. Please try again.');
        toast.error('Survey submission failed. Please try again.');
      } else {
        console.log('Survey submitted successfully, now sending confirmation email');
        
        try {
          const emailSent = await sendConfirmationEmail(
            data.contact_name, 
            data.email,
            data.company_name
          );
          
          if (emailSent) {
            console.log('Confirmation email sent successfully to', data.email);
          }
        } catch (emailError) {
          console.error('Error in email confirmation process:', emailError);
          // Continue with success path even if email fails
        }
        
        setSuccessMessage('Thank you for completing the survey!');
        toast.success('Survey submitted successfully!');
        window.scrollTo(0, 0);
      }
    } catch (err) {
      console.error('Error submitting survey:', err);
      setError('There was an error submitting your survey. Please try again.');
      toast.error('Survey submission failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  if (isLoading) {
    return (
      <div className="container mx-auto py-12 px-4">
        <div className="text-center">
          <p>Loading...</p>
        </div>
      </div>
    );
  }
  
  if (error && !isValid) {
    return (
      <div className="container mx-auto py-12 px-4">
        <div className="text-center">
          <p className="text-red-500" style={{ color: purpleTheme.error }}>{error}</p>
          <p className="mt-4">
            If you believe this is an error, please contact us for assistance.
          </p>
        </div>
      </div>
    );
  }
  
  if (successMessage) {
    return (
      <div className="container mx-auto py-12 px-4">
        <div className="text-center">
          <p className="text-green-600 mb-6">{successMessage}</p>
          <Button onClick={() => navigate('/')}>Return to Home</Button>
        </div>
      </div>
    );
  }
  
  const handleNextStep = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    
    if (currentStep === 1) {
      const isValid = await trigger(['contact_name', 'company_name', 'email', 'location']);
      if (!isValid) {
        toast.error('Please fill in all required fields');
        return;
      }
    }
    
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
    }
  };

  const handlePrevStep = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo(0, 0);
    }
  };

  return (
    <div className="container mx-auto py-12 px-4 max-w-3xl">
      <Seo
        title="Painter Survey — Quickatoo"
        description="Share how you quote and manage painting jobs so we can build Quickatoo around real contractor workflows."
        path="/survey"
        noindex
      />

      <div className="flex justify-center mb-8">
        <img 
          src="/lovable-uploads/c22d7cd3-d7b2-4b69-9087-2e231ebb8feb.png" 
          alt="Quickatoo Logo" 
          className="h-16" 
        />
      </div>
      
      {error && <p className="text-red-500 mb-4" style={{ color: purpleTheme.error }}>{error}</p>}
      
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {steps.map((step, index) => (
            <div key={step.id} className="contents">
              <div className="flex flex-col items-center">
                <div 
                  className={`flex items-center justify-center w-8 h-8 rounded-full border-2 stepper-circle
                    ${currentStep > step.id 
                      ? 'text-white border-transparent' 
                      : currentStep === step.id 
                        ? 'text-white border-transparent' 
                        : 'border-gray-300 text-gray-500'}`}
                  onClick={() => setCurrentStep(step.id)}
                  style={{ 
                    cursor: 'pointer',
                    background: currentStep > step.id 
                      ? purpleTheme.primary
                      : currentStep === step.id 
                        ? purpleTheme.secondary
                        : 'transparent'
                  }}
                >
                  {currentStep > step.id ? (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <span className="text-sm">{step.id}</span>
                  )}
                </div>
              </div>
              {index < steps.length - 1 && (
                <div 
                  className={`flex-1 h-1 stepper-line`}
                  style={{ 
                    background: currentStep > index + 2 
                      ? purpleTheme.primary
                      : currentStep > index + 1 
                        ? purpleTheme.gradient
                        : '#E2E8F0'
                  }}
                ></div>
              )}
            </div>
          ))}
        </div>
      </div>
      
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" noValidate>
        {currentStep === 1 && (
          <div className="p-6 rounded-lg shadow-sm" style={{ background: purpleTheme.cardBg }}>
            <h2 className="text-xl font-semibold mb-4" style={{ color: purpleTheme.primary }}>Contact Information</h2>
            
            <div className="space-y-6">
              <div className="space-y-1">
                <AnimatedInput
                  id="contact_name"
                  label="Your Name *"
                  {...register('contact_name', { required: 'Name is required' })}
                  style={{ 
                    '--input-border-color': errors.contact_name ? purpleTheme.error : purpleTheme.inputBorder,
                    '--input-focus-color': purpleTheme.inputFocus,
                  } as React.CSSProperties }
                />
                {errors.contact_name && (
                  <p className="text-sm" style={{ color: purpleTheme.error }}>
                    {errors.contact_name.message}
                  </p>
                )}
              </div>
              
              <div className="space-y-1">
                <AnimatedInput
                  id="company_name"
                  label="Company Name *"
                  {...register('company_name', { required: 'Company name is required' })}
                  style={{ 
                    '--input-border-color': errors.company_name ? purpleTheme.error : purpleTheme.inputBorder,
                    '--input-focus-color': purpleTheme.inputFocus,
                  } as React.CSSProperties }
                />
                {errors.company_name && (
                  <p className="text-sm" style={{ color: purpleTheme.error }}>
                    {errors.company_name.message}
                  </p>
                )}
              </div>
              
              <div className="space-y-1">
                <AnimatedInput
                  id="email"
                  type="email"
                  label="Email Address *"
                  {...register('email', { 
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address'
                    } 
                  })}
                  style={{ 
                    '--input-border-color': errors.email ? purpleTheme.error : purpleTheme.inputBorder,
                    '--input-focus-color': purpleTheme.inputFocus,
                  } as React.CSSProperties }
                />
                {errors.email && (
                  <p className="text-sm" style={{ color: purpleTheme.error }}>
                    {errors.email.message}
                  </p>
                )}
              </div>
              
              <AnimatedInput
                id="phone"
                type="tel"
                label="Phone Number"
                {...register('phone')}
                style={{ 
                  '--input-border-color': purpleTheme.inputBorder,
                  '--input-focus-color': purpleTheme.inputFocus,
                } as React.CSSProperties }
              />
              
              <div className="space-y-1">
                <AnimatedInput
                  id="location"
                  label="Location *"
                  {...register('location', { required: 'Location is required' })}
                  style={{ 
                    '--input-border-color': errors.location ? purpleTheme.error : purpleTheme.inputBorder,
                    '--input-focus-color': purpleTheme.inputFocus,
                  } as React.CSSProperties }
                />
                {errors.location && (
                  <p className="text-sm" style={{ color: purpleTheme.error }}>
                    {errors.location.message}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
        
        {currentStep === 2 && (
          <div className="p-6 rounded-lg shadow-sm" style={{ background: purpleTheme.cardBg }}>
            <h2 className="text-xl font-semibold mb-4" style={{ color: purpleTheme.primary }}>Business Information</h2>
            
            <div className="space-y-6">
              <Controller
                control={control}
                name="years_in_business"
                render={({ field }) => (
                  <AnimatedSelect
                    id="years_in_business"
                    label="Years in Business"
                    options={[
                      { value: "less_than_1", label: "Less than 1 year" },
                      { value: "1_to_3", label: "1-3 years" },
                      { value: "4_to_7", label: "4-7 years" },
                      { value: "8_to_15", label: "8-15 years" },
                      { value: "more_than_15", label: "More than 15 years" }
                    ]}
                    placeholder="Select years in business"
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
              
              <Controller
                control={control}
                name="team_size"
                render={({ field }) => (
                  <AnimatedSelect
                    id="team_size"
                    label="Team Size"
                    options={[
                      { value: "solo", label: "Solo (just me)" },
                      { value: "small", label: "Small (2-5 people)" },
                      { value: "medium", label: "Medium (6-15 people)" },
                      { value: "large", label: "Large (16+ people)" }
                    ]}
                    placeholder="Select team size"
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
              
              <div>
                <Label className="text-sm font-medium mb-2 block">
                  Business Type
                </Label>
                <div className="checkbox-group">
                  <Controller
                    name="business_type"
                    control={control}
                    render={({ field }) => (
                      <>
                        <div className="checkbox-label">
                          <Checkbox 
                            id="residential" 
                            checked={field.value?.includes('residential')}
                            onCheckedChange={(checked) => {
                              const updatedValue = checked 
                                ? [...(field.value || []), 'residential']
                                : (field.value || []).filter(value => value !== 'residential');
                              field.onChange(updatedValue);
                            }}
                          />
                          <Label htmlFor="residential" className="text-sm font-normal">Residential</Label>
                        </div>
                        <div className="checkbox-label">
                          <Checkbox 
                            id="commercial" 
                            checked={field.value?.includes('commercial')}
                            onCheckedChange={(checked) => {
                              const updatedValue = checked 
                                ? [...(field.value || []), 'commercial']
                                : (field.value || []).filter(value => value !== 'commercial');
                              field.onChange(updatedValue);
                            }}
                          />
                          <Label htmlFor="commercial" className="text-sm font-normal">Commercial</Label>
                        </div>
                        <div className="checkbox-label">
                          <Checkbox 
                            id="industrial" 
                            checked={field.value?.includes('industrial')}
                            onCheckedChange={(checked) => {
                              const updatedValue = checked 
                                ? [...(field.value || []), 'industrial']
                                : (field.value || []).filter(value => value !== 'industrial');
                              field.onChange(updatedValue);
                            }}
                          />
                          <Label htmlFor="industrial" className="text-sm font-normal">Industrial</Label>
                        </div>
                        <div className="checkbox-label">
                          <Checkbox 
                            id="new_construction" 
                            checked={field.value?.includes('new_construction')}
                            onCheckedChange={(checked) => {
                              const updatedValue = checked 
                                ? [...(field.value || []), 'new_construction']
                                : (field.value || []).filter(value => value !== 'new_construction');
                              field.onChange(updatedValue);
                            }}
                          />
                          <Label htmlFor="new_construction" className="text-sm font-normal">New Construction</Label>
                        </div>
                      </>
                    )}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
        
        {currentStep === 3 && (
          <div className="p-6 rounded-lg shadow-sm" style={{ background: purpleTheme.cardBg }}>
            <h2 className="text-xl font-semibold mb-4" style={{ color: purpleTheme.primary }}>Quoting Process</h2>
            
            <div className="space-y-6">
              <Controller
                control={control}
                name="quotes_per_month"
                render={({ field }) => (
                  <AnimatedSelect
                    id="quotes_per_month"
                    label="How many quotes do you prepare each month?"
                    options={[
                      { value: "less_than_5", label: "Less than 5" },
                      { value: "5_to_15", label: "5-15" },
                      { value: "16_to_30", label: "16-30" },
                      { value: "more_than_30", label: "More than 30" }
                    ]}
                    placeholder="Select number of quotes"
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
              
              <Controller
                control={control}
                name="time_per_quote"
                render={({ field }) => (
                  <AnimatedSelect
                    id="time_per_quote"
                    label="How much time do you spend on each quote?"
                    options={[
                      { value: "less_than_30_min", label: "Less than 30 minutes" },
                      { value: "30_min_to_1_hour", label: "30 minutes to 1 hour" },
                      { value: "1_to_2_hours", label: "1-2 hours" },
                      { value: "more_than_2_hours", label: "More than 2 hours" }
                    ]}
                    placeholder="Select time per quote"
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
              
              <div>
                <Label className="text-sm font-medium mb-2 block">
                  What are your main challenges with quotes? (Select all that apply)
                </Label>
                <div className="checkbox-group">
                  <Controller
                    name="quoting_challenges"
                    control={control}
                    render={({ field }) => (
                      <>
                        <div className="checkbox-label">
                          <Checkbox 
                            id="accurate_measurements" 
                            checked={field.value?.includes('accurate_measurements')}
                            onCheckedChange={(checked) => {
                              const updatedValue = checked 
                                ? [...(field.value || []), 'accurate_measurements']
                                : (field.value || []).filter(value => value !== 'accurate_measurements');
                              field.onChange(updatedValue);
                            }}
                          />
                          <Label htmlFor="accurate_measurements" className="text-sm font-normal">Getting accurate measurements</Label>
                        </div>
                        <div className="checkbox-label">
                          <Checkbox 
                            id="calculating_materials" 
                            checked={field.value?.includes('calculating_materials')}
                            onCheckedChange={(checked) => {
                              const updatedValue = checked 
                                ? [...(field.value || []), 'calculating_materials']
                                : (field.value || []).filter(value => value !== 'calculating_materials');
                              field.onChange(updatedValue);
                            }}
                          />
                          <Label htmlFor="calculating_materials" className="text-sm font-normal">Calculating materials needed</Label>
                        </div>
                        <div className="checkbox-label">
                          <Checkbox 
                            id="estimating_labor" 
                            checked={field.value?.includes('estimating_labor')}
                            onCheckedChange={(checked) => {
                              const updatedValue = checked 
                                ? [...(field.value || []), 'estimating_labor']
                                : (field.value || []).filter(value => value !== 'estimating_labor');
                              field.onChange(updatedValue);
                            }}
                          />
                          <Label htmlFor="estimating_labor" className="text-sm font-normal">Estimating labor hours</Label>
                        </div>
                        <div className="checkbox-label">
                          <Checkbox 
                            id="pricing_competitively" 
                            checked={field.value?.includes('pricing_competitively')}
                            onCheckedChange={(checked) => {
                              const updatedValue = checked 
                                ? [...(field.value || []), 'pricing_competitively']
                                : (field.value || []).filter(value => value !== 'pricing_competitively');
                              field.onChange(updatedValue);
                            }}
                          />
                          <Label htmlFor="pricing_competitively" className="text-sm font-normal">Pricing competitively</Label>
                        </div>
                        <div className="checkbox-label">
                          <Checkbox 
                            id="creating_professional_documents" 
                            checked={field.value?.includes('creating_professional_documents')}
                            onCheckedChange={(checked) => {
                              const updatedValue = checked 
                                ? [...(field.value || []), 'creating_professional_documents']
                                : (field.value || []).filter(value => value !== 'creating_professional_documents');
                              field.onChange(updatedValue);
                            }}
                          />
                          <Label htmlFor="creating_professional_documents" className="text-sm font-normal">Creating professional-looking documents</Label>
                        </div>
                        <div className="checkbox-label">
                          <Checkbox 
                            id="following_up" 
                            checked={field.value?.includes('following_up')}
                            onCheckedChange={(checked) => {
                              const updatedValue = checked 
                                ? [...(field.value || []), 'following_up']
                                : (field.value || []).filter(value => value !== 'following_up');
                              field.onChange(updatedValue);
                            }}
                          />
                          <Label htmlFor="following_up" className="text-sm font-normal">Following up with clients</Label>
                        </div>
                      </>
                    )}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
        
        {currentStep === 4 && (
          <div className="p-6 rounded-lg shadow-sm" style={{ background: purpleTheme.cardBg }}>
            <h2 className="text-xl font-semibold mb-4" style={{ color: purpleTheme.primary }}>Tools and Technology</h2>
            
            <div className="space-y-6">
              <div>
                <Label className="text-sm font-medium mb-2 block">
                  What quoting tools do you use now? (Select all that apply)
                </Label>
                <div className="checkbox-group">
                  <Controller
                    name="current_tools"
                    control={control}
                    render={({ field }) => (
                      <>
                        <div className="checkbox-label">
                          <Checkbox 
                            id="pen_paper" 
                            checked={field.value?.includes('pen_paper')}
                            onCheckedChange={(checked) => {
                              const updatedValue = checked 
                                ? [...(field.value || []), 'pen_paper']
                                : (field.value || []).filter(value => value !== 'pen_paper');
                              field.onChange(updatedValue);
                            }}
                          />
                          <Label htmlFor="pen_paper" className="text-sm font-normal">Pen and paper</Label>
                        </div>
                        <div className="checkbox-label">
                          <Checkbox 
                            id="spreadsheets" 
                            checked={field.value?.includes('spreadsheets')}
                            onCheckedChange={(checked) => {
                              const updatedValue = checked 
                                ? [...(field.value || []), 'spreadsheets']
                                : (field.value || []).filter(value => value !== 'spreadsheets');
                              field.onChange(updatedValue);
                            }}
                          />
                          <Label htmlFor="spreadsheets" className="text-sm font-normal">Spreadsheets (Excel, Google Sheets)</Label>
                        </div>
                        <div className="checkbox-label">
                          <Checkbox 
                            id="estimating_software" 
                            checked={field.value?.includes('estimating_software')}
                            onCheckedChange={(checked) => {
                              const updatedValue = checked 
                                ? [...(field.value || []), 'estimating_software']
                                : (field.value || []).filter(value => value !== 'estimating_software');
                              field.onChange(updatedValue);
                            }}
                          />
                          <Label htmlFor="estimating_software" className="text-sm font-normal">Estimating software</Label>
                        </div>
                      </>
                    )}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
        
        {currentStep === 5 && (
          <div className="p-6 rounded-lg shadow-sm" style={{ background: purpleTheme.cardBg }}>
            <h2 className="text-xl font-semibold mb-4" style={{ color: purpleTheme.primary }}>Feedback Opportunities</h2>
            
            <div className="space-y-6">
              <div>
                <Label className="text-sm font-medium mb-2 block">
                  Would you provide feedback during our beta period?
                </Label>
                <div className="space-y-2 mt-2">
                  <div className="flex items-center">
                    <input
                      type="radio"
                      id="feedback_yes"
                      name="provide_beta_feedback"
                      value="yes"
                      className="mr-2 h-4 w-4 accent-purple-600"
                      {...register('provide_beta_feedback')}
                    />
                    <Label htmlFor="feedback_yes" className="text-sm font-normal">Yes, absolutely</Label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="radio"
                      id="feedback_no"
                      name="provide_beta_feedback"
                      value="no"
                      className="mr-2 h-4 w-4 accent-purple-600"
                      {...register('provide_beta_feedback')}
                    />
                    <Label htmlFor="feedback_no" className="text-sm font-normal">No</Label>
                  </div>
                </div>
              </div>
              
              <div>
                <Label className="text-sm font-medium mb-2 block">
                  How do you prefer to share feedback?
                </Label>
                <div className="space-y-2 mt-2">
                  <div className="flex items-center">
                    <input
                      type="radio"
                      id="method_email"
                      name="feedback_method"
                      value="email"
                      className="mr-2 h-4 w-4 accent-purple-600"
                      {...register('feedback_method')}
                    />
                    <Label htmlFor="method_email" className="text-sm font-normal">Email</Label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="radio"
                      id="method_phone"
                      name="feedback_method"
                      value="phone"
                      className="mr-2 h-4 w-4 accent-purple-600"
                      {...register('feedback_method')}
                    />
                    <Label htmlFor="method_phone" className="text-sm font-normal">Phone call</Label>
                  </div>
                </div>
              </div>

              <div>
                <Label htmlFor="additional_comments" className="text-sm font-medium mb-2 block">
                  Any other comments or questions?
                </Label>
                <Textarea
                  id="additional_comments"
                  className="w-full rounded-md p-2 bg-white border-input hover:border-[#7c3aed]/50 focus:border-[#7c3aed] focus:ring-[#7c3aed]/20 focus:ring-[3px] transition-all duration-200 focus:outline-none"
                  placeholder="Any additional comments..."
                  {...register('additional_comments')}
                />
              </div>
            </div>
          </div>
        )}
        
        <div className="flex justify-between mt-8">
          <Button 
            type="button" 
            variant="outline" 
            onClick={handlePrevStep}
            disabled={currentStep === 1}
            className="prev-button"
            style={{ 
              borderColor: purpleTheme.primary,
              color: purpleTheme.primary
            }}
          >
            Previous
          </Button>
          
          {currentStep < steps.length ? (
            <Button 
              type="button" 
              onClick={handleNextStep}
              className="next-button"
              style={{ 
                background: purpleTheme.gradient,
                boxShadow: '0 4px 6px rgba(57, 38, 177, 0.2)'
              }}
            >
              Next
            </Button>
          ) : (
            <Button 
              type="submit" 
              disabled={isSubmitting}
              className="submit-button"
              style={{ 
                background: purpleTheme.gradient,
                boxShadow: '0 4px 6px rgba(57, 38, 177, 0.2)',
                opacity: isSubmitting ? 0.7 : 1
              }}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Survey'}
            </Button>
          )}
        </div>
      </form>
    </div>
  );
};

export default Survey;
